import { Schema, type DOMOutputSpec, type MarkSpec, type NodeSpec } from 'prosemirror-model';
import { LEGACY_ENTRY_LINK } from '../security/sanitize.ts';

/**
 * What an entry's rich text can contain (docs/ENTRY-EDITOR-PLAN.md section 4.2): the
 * formatting of the desktop's HTMLEditor and nothing more. Reading accepts the forms
 * WebKit's editing commands write (`<b>`, inline styles, and `<font>`, which html.ts turns
 * into styles first); writing always uses
 * one form (`<strong>`, `<em>`, `<span style>`). Anything else is dropped by the parser,
 * which is why the editor refuses content it cannot represent (`unrepresentable` in
 * html.ts) rather than editing it.
 */

const ALIGNMENTS = new Set(['left', 'center', 'right', 'justify']);

function alignmentOf(dom: HTMLElement): string | null {
  const value = (dom.style.textAlign || dom.getAttribute('align') || '').toLowerCase();
  return ALIGNMENTS.has(value) ? value : null;
}

function alignedAttrs(dom: HTMLElement): { align: string | null } {
  return { align: alignmentOf(dom) };
}

function aligned(tag: string, align: unknown): DOMOutputSpec {
  return typeof align === 'string' && ALIGNMENTS.has(align) ? [tag, { style: `text-align: ${align}` }, 0] : [tag, 0];
}

/** Link targets that may be written: the same schemes the sanitizer keeps (security/sanitize.ts). */
export const LINK_SCHEMES = /^(?:https?:|mailto:|realmery:entry\/)/i;

/** A safe colour: names, hex and rgb()/rgba() only, so a style value can't smuggle in a url(). */
const COLOR = /^(?:#[0-9a-f]{3,8}|rgba?\(\s*[\d.\s,%]+\)|[a-z]{3,20})$/i;
// At least one letter: the desktop's HTMLEditor writes an empty `font-family: ""` on almost every span.
const FONT_FAMILY = /^(?=.*[a-z])[\w\s,'"-]{1,120}$/i;
const FONT_SIZE = /^(?:\d{1,3}(?:\.\d+)?(?:px|pt|em|rem|%)|xx-small|x-small|small|medium|large|x-large|xx-large|xxx-large)$/i;

function checked(value: string | null | undefined, pattern: RegExp): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed !== '' && pattern.test(trimmed) ? trimmed : null;
}

const blocks = { content: 'inline*', group: 'block', attrs: { align: { default: null } } } as const;

const nodes: Record<string, NodeSpec> = {
  doc: { content: 'block+' },
  paragraph: {
    ...blocks,
    parseDOM: [{ tag: 'p', getAttrs: alignedAttrs }, { tag: 'div', getAttrs: alignedAttrs }],
    toDOM: (node) => aligned('p', node.attrs['align']),
  },
  heading: {
    ...blocks,
    attrs: { level: { default: 1 }, align: { default: null } },
    defining: true,
    parseDOM: [1, 2, 3, 4, 5, 6].map((level) => ({ tag: `h${level}`, getAttrs: (dom: HTMLElement) => ({ level, ...alignedAttrs(dom) }) })),
    toDOM: (node) => aligned(`h${node.attrs['level'] as number}`, node.attrs['align']),
  },
  blockquote: {
    content: 'block+',
    group: 'block',
    defining: true,
    parseDOM: [{ tag: 'blockquote' }],
    toDOM: () => ['blockquote', 0],
  },
  horizontal_rule: { group: 'block', parseDOM: [{ tag: 'hr' }], toDOM: () => ['hr'] },
  bullet_list: { content: 'list_item+', group: 'block', parseDOM: [{ tag: 'ul' }], toDOM: () => ['ul', 0] },
  ordered_list: { content: 'list_item+', group: 'block', parseDOM: [{ tag: 'ol' }], toDOM: () => ['ol', 0] },
  list_item: { content: 'paragraph block*', defining: true, parseDOM: [{ tag: 'li' }], toDOM: () => ['li', 0] },
  text: { group: 'inline' },
  hard_break: { inline: true, group: 'inline', selectable: false, parseDOM: [{ tag: 'br' }], toDOM: () => ['br'] },
};

const marks: Record<string, MarkSpec> = {
  link: {
    attrs: { href: {} },
    inclusive: false,
    parseDOM: [
      {
        tag: 'a[href]',
        getAttrs: (dom: HTMLElement) => {
          const href = (dom.getAttribute('href') ?? '').trim().replace(LEGACY_ENTRY_LINK, 'realmery:entry/');
          return LINK_SCHEMES.test(href) ? { href } : false;
        },
      },
    ],
    // Mention links carry the desktop's class (HtmlMentions.LINK_CLASS), which its skins style.
    toDOM: (mark) => {
      const href = mark.attrs['href'] as string;
      return ['a', href.startsWith('realmery:entry/') ? { href, class: 'lf-mention' } : { href }, 0];
    },
  },
  bold: {
    parseDOM: [
      { tag: 'strong' },
      // Google Docs wraps everything in <b style="font-weight: normal">.
      { tag: 'b', getAttrs: (dom: HTMLElement) => dom.style.fontWeight !== 'normal' && null },
      { style: 'font-weight', getAttrs: (value: string) => /^(?:bold(?:er)?|[6-9]00)$/.test(value) && null },
    ],
    toDOM: () => ['strong', 0],
  },
  italic: {
    parseDOM: [{ tag: 'em' }, { tag: 'i' }, { style: 'font-style=italic' }],
    toDOM: () => ['em', 0],
  },
  underline: {
    parseDOM: [{ tag: 'u' }, { style: 'text-decoration', getAttrs: (value: string) => value.includes('underline') && null }],
    toDOM: () => ['u', 0],
  },
  strike: {
    parseDOM: [
      { tag: 's' },
      { tag: 'strike' },
      { tag: 'del' },
      { style: 'text-decoration', getAttrs: (value: string) => value.includes('line-through') && null },
    ],
    toDOM: () => ['s', 0],
  },
  text_color: {
    attrs: { color: {} },
    parseDOM: [
      { style: 'color', getAttrs: (value: string) => colorAttrs(value) },
    ],
    toDOM: (mark) => ['span', { style: `color: ${mark.attrs['color'] as string}` }, 0],
  },
  background_color: {
    attrs: { color: {} },
    parseDOM: [
      { style: 'background-color', getAttrs: (value: string) => colorAttrs(value) },
      { style: 'background', getAttrs: (value: string) => colorAttrs(value) },
    ],
    toDOM: (mark) => ['span', { style: `background-color: ${mark.attrs['color'] as string}` }, 0],
  },
  font_family: {
    attrs: { family: {} },
    parseDOM: [
      { style: 'font-family', getAttrs: (value: string) => familyAttrs(value) },
    ],
    toDOM: (mark) => ['span', { style: `font-family: ${mark.attrs['family'] as string}` }, 0],
  },
  font_size: {
    attrs: { size: {} },
    parseDOM: [
      { style: 'font-size', getAttrs: (value: string) => sizeAttrs(value) },
    ],
    toDOM: (mark) => ['span', { style: `font-size: ${mark.attrs['size'] as string}` }, 0],
  },
};

function colorAttrs(value: string | null): { color: string } | false {
  const color = checked(value, COLOR);
  return color && color.toLowerCase() !== 'transparent' && color.toLowerCase() !== 'inherit' ? { color } : false;
}

function familyAttrs(value: string | null): { family: string } | false {
  const family = checked(value, FONT_FAMILY);
  return family ? { family } : false;
}

function sizeAttrs(value: string | null): { size: string } | false {
  const size = checked(value, FONT_SIZE);
  return size ? { size } : false;
}

export const entrySchema = new Schema({ nodes, marks });
